import { IPageSettings } from "../../interfaces/IPageSettings";
import { IStoreItem } from "../../interfaces/IStoredItem";
import { FileTypeEnum } from "../enums/fileTypeEnum";
import { FolderTypeIconEnum } from "../enums/FolderTypeIconEnum";
import { isTeamsChannelLibraryRootLocation, normalizeTeamsChannelLibraryRootLocation } from "./teamsChannelLocationHelper";

const savingComponentType = "Saving";
const oneDriveComponentType = "OneDrive";

export class StoreItemHelper {
    static normalizeLocations = (locations?: IPageSettings[]): IPageSettings[] | undefined => {
        if (!Array.isArray(locations)) {
            return locations;
        }

        let didNormalize = false;
        const normalizedLocations = locations.map(location => {
            const normalizedLocation = normalizeTeamsChannelLibraryRootLocation(location);
            didNormalize = didNormalize || normalizedLocation !== location;
            return normalizedLocation;
        });

        return didNormalize ? normalizedLocations : locations;
    }

    static normalizeStoreItem = (item: IStoreItem): IStoreItem => {
        if (!item) {
            return item;
        }

        const locations = StoreItemHelper.normalizeLocations(item.locations);
        const finalLocation = locations?.filter(location => location.type !== savingComponentType).pop();
        const isTeamsChannelLibraryRoot = isTeamsChannelLibraryRootLocation(finalLocation);
        const isOneDriveRoot = finalLocation?.type === oneDriveComponentType && !finalLocation.parentId;

        if (locations === item.locations && !isTeamsChannelLibraryRoot && !isOneDriveRoot) {
            return item;
        }

        return {
            ...item,
            locations,
            itemId: isTeamsChannelLibraryRoot || isOneDriveRoot ? null : item.itemId,
            folderId: isTeamsChannelLibraryRoot || isOneDriveRoot ? null : item.folderId,
            driveId: isTeamsChannelLibraryRoot || isOneDriveRoot ? finalLocation?.id ?? item.driveId : item.driveId,
            folderTypeIcon: isOneDriveRoot ? FolderTypeIconEnum.oneDrive : item.folderTypeIcon,
            sharePointUniqueId: isTeamsChannelLibraryRoot ? undefined : item.sharePointUniqueId,
        };
    }

    static normalizeStoreItems = (items?: IStoreItem[]): IStoreItem[] | undefined =>
        Array.isArray(items) ? items.map(StoreItemHelper.normalizeStoreItem) : items;

    static removeSaveLocation = (locations: IPageSettings[]) => {
        if (locations.length && locations.length > 0) {
            const lastItem: IPageSettings = locations[locations.length - 1];
            const popLastItem: boolean = lastItem.type === savingComponentType;

            if (popLastItem) {
                locations.pop();
            }
        }
    }

    static itemExists = (existingItem: IStoreItem, newItems: IStoreItem[]): boolean => {
        if (!newItems) return false;
        return existingItem.fileType === FileTypeEnum.file
            ? newItems.some(newItem => newItem.itemId === existingItem.itemId)
            : StoreItemHelper.folderExists(existingItem, newItems);
    }

    static folderExists = (existingItem: IStoreItem, newItems: IStoreItem[]): boolean => {
        const isRoot = !existingItem?.itemId;
        const result = isRoot
            ? newItems.some(newItem => newItem.driveId === existingItem.driveId && newItem.name === existingItem.name)
            : newItems.some(newItem => newItem.driveId === existingItem.driveId && newItem.itemId === existingItem.itemId);

        return result;
    }

    static sortByConversationAndDateDesc = (a: IStoreItem, b: IStoreItem, consersationId: string) => {
        const isAPriority = a.conversationId === consersationId;
        const isBPriority = b.conversationId === consersationId;

        if (isAPriority && !isBPriority) {
            return -1;
        }
        if (!isAPriority && isBPriority) {
            return 1;
        }

        return this.sortByDateDesc(a, b);
    }

    static sortByDateDesc = (a: IStoreItem, b: IStoreItem) => {
        if (a?.datePinned && b?.datePinned) {
            return new Date(b.datePinned).getTime() - new Date(a.datePinned).getTime();
        }
        if (a?.datePinned) {
            return -1;
        }
        if (b?.datePinned) {
            return 1;
        }
        return 0;
    }

    static getExistingItem = (existingItems: IStoreItem[], newItem: IStoreItem): IStoreItem => {
        if (!existingItems) return null;
        return newItem.fileType === FileTypeEnum.file ? existingItems.find(item => item.itemId === newItem.itemId) : StoreItemHelper.getExistingFolder(existingItems, newItem);
    }

    private static getExistingFolder = (existingItems: IStoreItem[], newItem: IStoreItem): IStoreItem => {
        const isRoot = !newItem?.itemId;
        const result = isRoot
            ? existingItems.find(item => item.driveId === newItem.driveId && item.name === newItem.name)
            : existingItems.find(item => item.driveId === newItem.driveId && item.itemId === newItem.itemId);

        return result;
    }
}
